import React from "react";
import { Panel } from "./Panels";
import type { DashboardData } from "./useDashboardData";
import type { WorkerSandboxStatusView } from "../api";
import { dashboardStatusErrorCopy } from "./dashboardStatusErrorCopy";
import { LearningProposalsPanel } from "./CommandPanels";

function boolLabel(value: boolean | null | undefined, yes: string, no: string, pending = "-"): string {
  if (value == null) return pending;
  return value ? yes : no;
}

function tileAccent(status: "ok" | "warn" | "down" | "neutral"): string {
  if (status === "ok") return "simo-accent-ok";
  if (status === "warn") return "simo-accent-warn";
  if (status === "down") return "simo-accent-down";
  return "simo-accent-neutral";
}

function WorkerLine({ worker }: { worker: WorkerSandboxStatusView["workers"][number] }): JSX.Element {
  const accent = worker.available ? "simo-accent-ok" : "simo-accent-confirming";
  return (
    <li className="rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-1.5">
      <div className="flex items-center gap-2">
        <span className={`status-dot ${accent}`} />
        <span className="min-w-0 flex-1 truncate text-xs text-jarvis-text" title={worker.label}>{worker.label}</span>
        <span className="badge shrink-0 bg-jarvis-bg/60 text-jarvis-muted">{worker.mode}</span>
      </div>
      <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] text-jarvis-muted">
        <span>Identity</span>
        <span className="truncate text-right font-mono" title={worker.identityRef}>{worker.identityRef}</span>
        <span>Write Path</span>
        <span className="text-right">{worker.writePath}</span>
        <span>Secrets</span>
        <span className="text-right">{worker.canReadSecrets ? "lesbar" : "blockiert"}</span>
        <span>Shell/Push</span>
        <span className="text-right">{worker.prodShellAllowed || worker.canPush ? "erlaubt" : "blockiert"}</span>
      </div>
    </li>
  );
}

function compactAge(value: string | null | undefined): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 16);
  return date.toISOString().slice(11, 16);
}

function ConsoleMetric({
  label,
  value,
  detail,
  accent,
}: {
  label: string;
  value: string;
  detail: string;
  accent: "ok" | "warn" | "down" | "neutral";
}): JSX.Element {
  return (
    <div className={`rounded-md border border-jarvis-border/55 bg-jarvis-bg/35 px-2 py-1.5 ${tileAccent(accent)}`}>
      <div className="flex items-center gap-1 text-[9px] uppercase text-jarvis-muted">
        <span className="status-dot" style={{ width: 5, height: 5 }} />
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 truncate text-lg leading-none text-jarvis-text" title={value}>{value}</div>
      <div className="mt-1 truncate text-[10px] text-jarvis-muted" title={detail}>{detail}</div>
    </div>
  );
}

export function OperatorConsolePanel({ data }: { data: DashboardData }): JSX.Element {
  const pendingApprovals = data.approvalInbox?.items.filter((item) => item.status === "pending") ?? [];
  // Slice 3 — kanonisch: Mission-Runs (control_plane_runs), keine Chat-Runs.
  const activeRunsCount = data.controlPlaneSummary?.semantics?.runs.nonTerminal ?? 0;
  const latestRun = data.runs[0] ?? null;
  const latestEvidence = data.gatedEvidence[0] ?? null;
  const availableWorkers = data.runnerBoundary?.workers.filter((worker) => worker.available).length ?? 0;
  const projectStartGate =
    data.agentRegistry?.actionGates?.canStartRun === true &&
    data.runnerBoundary?.safeExecutionEnabled === true &&
    data.approvalInbox?.actionGates?.requiresOwnerSession === true;
  const executorBlocked =
    data.agentRegistry?.actionGates?.shellGitDeploy === false &&
    data.approvalInbox?.actionGates?.autoExecute === false &&
    data.runnerBoundary?.executionEnabled === false;
  const errors = Object.keys(data.errors).length;
  const accent = errors > 0 ? "simo-accent-warn" : pendingApprovals.length > 0 ? "simo-accent-confirming" : "simo-accent-ok";

  const workflowSteps = [
    { label: "Projekt", value: projectStartGate ? "bereit" : "gated" },
    { label: "Plan", value: data.agentRegistry?.routingModel?.dryRunOnly === true ? "dry-run" : "policy" },
    { label: "Approval", value: pendingApprovals.length > 0 ? `${pendingApprovals.length} offen` : "klar" },
    { label: "Evidence", value: latestEvidence ? latestEvidence.resultStatus : "leer" },
  ];

  return (
    <Panel
      title="Operator Console"
      accent={accent}
      action={<span className="kpi text-xs text-jarvis-muted">{projectStartGate ? "READY" : "GATED"}</span>}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <ConsoleMetric
            label="Projektstart"
            value={projectStartGate ? "bereit" : "gated"}
            detail={data.agentRegistry?.routingModel?.policyGateRequired ? "Policy Gate aktiv" : "Agent Gate pruefen"}
            accent={projectStartGate ? "ok" : "warn"}
          />
          <ConsoleMetric
            label="Workflows"
            value={String(activeRunsCount)}
            detail={latestRun ? `${latestRun.status} ${compactAge(latestRun.updatedAt)}` : "keine aktiven Mission-Runs"}
            accent={activeRunsCount > 0 ? "warn" : "ok"}
          />
          <ConsoleMetric
            label="Approvals"
            value={String(pendingApprovals.length)}
            detail={data.approvalInbox?.actionGates?.statusOnly ? "Status-only" : "Gate wird geladen"}
            accent={pendingApprovals.length > 0 ? "warn" : "ok"}
          />
          <ConsoleMetric
            label="Runner"
            value={String(availableWorkers)}
            detail={data.runnerBoundary?.safeExecutePolicyId ?? "Boundary pending"}
            accent={data.runnerBoundaryError ? "down" : availableWorkers > 0 ? "ok" : "neutral"}
          />
        </div>

        <div className="grid gap-2 md:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-md border border-jarvis-border/50 bg-jarvis-bg/25 px-2 py-2">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider text-jarvis-muted">Workflow Lane</span>
              <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{executorBlocked ? "No auto execute" : "Policy pruefen"}</span>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {workflowSteps.map((step) => (
                <div key={step.label} className="min-w-0 rounded-md border border-jarvis-border/40 bg-jarvis-surface/45 px-1.5 py-1.5 text-center">
                  <div className="truncate text-[9px] uppercase text-jarvis-muted">{step.label}</div>
                  <div className="mt-1 truncate text-[11px] text-jarvis-text" title={step.value}>{step.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-jarvis-border/50 bg-jarvis-bg/25 px-2 py-2">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider text-jarvis-muted">Evidence</span>
              <span className="badge bg-jarvis-bg/60 text-jarvis-muted">{data.gatedEvidence.length}</span>
            </div>
            {latestEvidence ? (
              <div className="space-y-1 text-[10px] text-jarvis-muted">
                <div className="truncate font-mono text-jarvis-text" title={latestEvidence.actionId}>{latestEvidence.actionId}</div>
                <div className="flex justify-between gap-2">
                  <span>{latestEvidence.resultStatus}</span>
                  <span className="truncate font-mono" title={latestEvidence.approvalId ?? ""}>{latestEvidence.approvalId ?? "no approval"}</span>
                </div>
              </div>
            ) : (
              <div className="text-[11px] text-jarvis-muted">Noch keine Evidence im Snapshot.</div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
          <span className="badge bg-jarvis-bg/60">Projektstart policy-gated</span>
          <span className="badge bg-jarvis-bg/60">Approvals entscheiden</span>
          <span className="badge bg-jarvis-bg/60">Runner/Worker sichtbar</span>
          <span className="badge bg-jarvis-bg/60">Logs/Evidence sichtbar</span>
          <span className="badge bg-jarvis-bg/60">Shell/Git/Deploy gated</span>
        </div>
      </div>
    </Panel>
  );
}

/**
 * Operations Center actionability foundation. The panel deliberately exposes
 * the same governed review queue that owns proposal/apply decisions; it does
 * not invent a second mutation path or claim that every registry surface is
 * already fully reversible.
 */
export function OperationsActionFlowPanel({ data, onOpenAgentOs }: { data: DashboardData; onOpenAgentOs?: () => void }): JSX.Element {
  const pending = data.learningProposals.filter((proposal) => proposal.status === "pending");
  // Slice 3 — kanonisch: control_plane_runs non-terminal; UNAVAILABLE wenn kein Summary.
  const activeRuns = data.controlPlaneSummary?.semantics?.runs.nonTerminal ?? null;
  const stages = [
    { label: "Entwurf", value: pending.length > 0 ? `${pending.length} offen` : "bereit", tone: pending.length > 0 ? "warn" : "ok" },
    { label: "Diff", value: pending.length > 0 ? "reviewbereit" : "wartet", tone: pending.length > 0 ? "ok" : "neutral" },
    { label: "Owner-Review", value: pending.length > 0 ? "erforderlich" : "leer", tone: pending.length > 0 ? "warn" : "neutral" },
    { label: "Apply/Sync", value: "gated", tone: "neutral" },
    { label: "Audit/Stop", value: activeRuns === null ? "UNAVAILABLE" : activeRuns > 0 ? `${activeRuns} Run(s)` : "bereit", tone: activeRuns && activeRuns > 0 ? "warn" : "ok" },
  ] as const;

  return (
    <Panel
      title="Operations Flow"
      accent={pending.length > 0 ? "simo-accent-confirming" : "simo-accent-neutral"}
      action={<span className="kpi text-xs text-jarvis-muted">{pending.length > 0 ? "REVIEW" : "READY"}</span>}
    >
      <div className="space-y-2">
        <p className="text-[11px] leading-relaxed text-jarvis-muted">
          Jede Änderung bleibt im gemeinsamen Owner-Gate: Entwurf → Diff → Review → Apply/Sync → Audit oder sicherer Stop.
          Es gibt keinen zweiten direkten Mutationspfad.
        </p>
        <ol aria-label="Operations Action Flow" className="grid grid-cols-2 gap-1.5 md:grid-cols-5">
          {stages.map((stage, index) => (
            <li key={stage.label} className={`rounded-md border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-1.5 ${tileAccent(stage.tone)}`}>
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-jarvis-muted">
                <span>{index + 1}</span>
                <span className="truncate">{stage.label}</span>
              </div>
              <div className="mt-1 truncate text-[11px] text-jarvis-text" title={stage.value}>{stage.value}</div>
            </li>
          ))}
        </ol>
        <LearningProposalsPanel data={data} onOpenAgentOs={onOpenAgentOs} />
      </div>
    </Panel>
  );
}

export function ControlCenterSummaryPanel({ data }: { data: DashboardData }): JSX.Element {
  const pendingApprovals = data.approvalInbox?.items.filter((item) => item.status === "pending").length ?? data.overview?.approvals.pending ?? 0;
  // Slice 3 — kanonisch: control_plane_runs non-terminal; UNAVAILABLE wenn kein Summary.
  const activeRuns = data.controlPlaneSummary?.semantics?.runs.nonTerminal ?? null;
  const runnerErrors = data.overview?.runs.error ?? data.runs.filter((run) => run.status === "error").length;
  const activeAgents = data.agentRegistry?.agents.filter((agent) => agent.executionState === "active").length ?? data.agents.filter((agent) => agent.liveStatus === "active").length;
  const workerExecutionOff = data.workerSandbox?.runtimeHardening.execution.enabled === false;
  const evidenceEvents = data.timeline.filter((event) => event.type === "evidence").length;
  const errorCount = Object.keys(data.errors).length;

  const tiles = [
    {
      label: "API",
      value: data.health == null ? "-" : data.health.ok ? "online" : "degraded",
      detail: data.health?.runtime?.commit?.slice(0, 7) ?? "kein Commit",
      status: data.health == null ? "neutral" : data.health.ok ? "ok" : "warn",
    },
    {
      label: "Owner Auth",
      value: boolLabel(data.ownerAuthStatus?.ownerAuth.configured, "konfiguriert", "offen"),
      detail: data.ownerAuthStatus?.ownerAuth.keyId ?? data.ownerAuthError?.requestId ?? "keine Key-ID",
      status: data.ownerAuthError ? "down" : data.ownerAuthStatus?.ownerAuth.configured ? "ok" : "warn",
    },
    {
      label: "Approvals",
      value: String(pendingApprovals),
      detail: data.approvalInbox?.actionGates?.canExecute === false ? "Status-only" : "Gate pruefen",
      status: pendingApprovals > 0 ? "warn" : "ok",
    },
    {
      label: "Agents",
      value: String(data.agentRegistry?.agents.length ?? data.agents.length),
      detail: activeAgents > 0 ? `${activeAgents} laeuft` : "policy-bound",
      status: activeAgents > 0 ? "warn" : "ok",
    },
    {
      label: "Runner",
      value: activeRuns === null ? "UNAVAILABLE" : String(activeRuns),
      detail: runnerErrors > 0 ? `${runnerErrors} Fehler` : "dry-run/read-only",
      status: runnerErrors > 0 ? "down" : activeRuns && activeRuns > 0 ? "warn" : "ok",
    },
    {
      label: "Evidence",
      value: String(evidenceEvents),
      detail: data.auditOk === false ? "Audit gebrochen" : `${data.timeline.length} Events`,
      status: data.auditOk === false ? "down" : evidenceEvents > 0 ? "ok" : "neutral",
    },
  ] as const;

  return (
    <Panel
      title="Control Center"
      accent={errorCount > 0 ? "simo-accent-warn" : "simo-accent-ok"}
      action={<span className="kpi text-xs text-jarvis-muted">{errorCount === 0 ? "GO" : `${errorCount} Warn`}</span>}
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
        {tiles.map((tile) => (
          <div key={tile.label} className={`rounded-md border border-jarvis-border/60 bg-jarvis-bg/35 p-2 ${tileAccent(tile.status)}`}>
            <div className="flex items-center gap-1 text-[9px] uppercase text-jarvis-muted">
              <span className="status-dot" style={{ width: 5, height: 5 }} />
              <span className="truncate">{tile.label}</span>
            </div>
            <div className="mt-1 truncate text-base leading-none text-jarvis-text" title={tile.value}>{tile.value}</div>
            <div className="mt-1 truncate font-mono text-[10px] text-jarvis-muted" title={tile.detail}>{tile.detail}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
        <span className="badge bg-jarvis-bg/60">Worker Execute {workerExecutionOff ? "aus" : "pruefen"}</span>
        <span className="badge bg-jarvis-bg/60">Connector Writes gated</span>
        <span className="badge bg-jarvis-bg/60">Shell/Git/Deploy gated</span>
      </div>
    </Panel>
  );
}

export function WorkerStatusPanel({
  status,
  error,
}: {
  status: WorkerSandboxStatusView | null;
  error: Error | null;
}): JSX.Element {
  const executionOff = status?.runtimeHardening.execution.enabled === false;
  const hardeningOk =
    status != null &&
    status.readOnlyByDefault &&
    !status.directProdShellAllowed &&
    !status.directHostWriteAllowed &&
    executionOff &&
    status.runtimeHardening.workerIdentity.required &&
    status.runtimeHardening.heartbeat.required &&
    status.runtimeHardening.jobLease.required &&
    status.runtimeHardening.retry.requiresPolicyRecheck &&
    !status.runtimeHardening.retry.retryUnsafeActions &&
    status.runtimeHardening.deadLetter.enabled;
  const accent = error ? "simo-accent-error" : hardeningOk ? "simo-accent-ok" : "simo-accent-warn";

  return (
    <Panel
      title="Worker Status"
      accent={accent}
      action={<span className="kpi text-xs text-jarvis-muted">{status?.workers.length ?? "-"}</span>}
    >
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-jarvis-muted">
          <span>Execution</span>
          <span className="text-right">{executionOff ? "aus" : "aktiv"}</span>
          <span>Worktree</span>
          <span className="text-right">{boolLabel(status?.worktree.configured, "konfiguriert", "nicht konfiguriert")}</span>
          <span>Heartbeat</span>
          <span className="text-right">{boolLabel(status?.runtimeHardening.heartbeat.required, "pflicht", "aus")}</span>
          <span>Dead-letter</span>
          <span className="text-right">{boolLabel(status?.runtimeHardening.deadLetter.enabled, "an", "aus")}</span>
          <span>Wiederholung</span>
          <span className="text-right">{status?.runtimeHardening.retry.requiresPolicyRecheck ? "Policy-Recheck" : "-"}</span>
          <span>Secrets</span>
          <span className="text-right">{status?.runtimeHardening.workerIdentity.rendererCredentialAccess ? "Renderer-Zugriff" : "blockiert"}</span>
        </div>
        {error && <div className="text-[10px] text-rose-300 break-words">{dashboardStatusErrorCopy(error, "worker-status")}</div>}
        {!status ? (
          <div className="text-[11px] text-jarvis-muted">Worker-Status wird geladen.</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
              <span className="badge bg-jarvis-bg/60">{status.policyVersion}</span>
              <span className="badge bg-jarvis-bg/60">Lease {Math.round(status.runtimeHardening.jobLease.ttlMs / 1000)}s</span>
              <span className="badge bg-jarvis-bg/60">Timeout {Math.round(status.runtimeHardening.timeout.defaultJobTimeoutMs / 1000)}s</span>
            </div>
            <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1 simo-thin-scroll">
              {status.workers.map((worker) => <WorkerLine key={worker.id} worker={worker} />)}
            </ul>
          </>
        )}
      </div>
    </Panel>
  );
}
